import { CalendarDays, Check, Clock3, FileText, Hotel, Plane, X } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import { useBookingDashboard } from './booking-dashboard-store.ts';

const messages = defineMessages({
  title: {
    id: 'travellerDashboard.title',
    defaultMessage: 'Traveller dashboard',
    description: 'Heading for the account dashboard drawer',
  },
  close: {
    id: 'travellerDashboard.close',
    defaultMessage: 'Close traveller dashboard',
    description: 'Accessible label for closing the account dashboard drawer',
  },
  emptyTitle: {
    id: 'travellerDashboard.emptyTitle',
    defaultMessage: 'Your trip will live here',
    description: 'Heading for the empty traveller dashboard',
  },
  emptyBody: {
    id: 'travellerDashboard.emptyBody',
    defaultMessage: 'Choose a destination and a plan first. The flight, stay and itinerary handoff will appear here as you move through the demo.',
    description: 'Body for the empty traveller dashboard',
  },
  currentTrip: {
    id: 'travellerDashboard.currentTrip',
    defaultMessage: 'Current trip',
    description: 'Label above the current trip summary',
  },
  status: {
    id: 'travellerDashboard.status',
    defaultMessage: 'Status',
    description: 'Label above the booking status list',
  },
  flight: {
    id: 'travellerDashboard.flight',
    defaultMessage: 'Flight ticket',
    description: 'Flight stage label in the dashboard',
  },
  hotel: {
    id: 'travellerDashboard.hotel',
    defaultMessage: 'Best-fit hotel',
    description: 'Hotel stage label in the dashboard',
  },
  details: {
    id: 'travellerDashboard.details',
    defaultMessage: 'Trip details & itinerary',
    description: 'Trip details stage label in the dashboard',
  },
  ready: {
    id: 'travellerDashboard.ready',
    defaultMessage: 'Ready',
    description: 'Status shown after the demo flow is complete',
  },
  selected: {
    id: 'travellerDashboard.selected',
    defaultMessage: 'Selected',
    description: 'Status shown after a simulated stage is selected',
  },
  inProgress: {
    id: 'travellerDashboard.inProgress',
    defaultMessage: 'In progress',
    description: 'Status shown for the current simulated stage',
  },
  next: {
    id: 'travellerDashboard.next',
    defaultMessage: 'Up next',
    description: 'Status shown for a future simulated stage',
  },
  itinerary: {
    id: 'travellerDashboard.itinerary',
    defaultMessage: 'Itinerary snapshot',
    description: 'Heading above the dashboard itinerary',
  },
  demo: {
    id: 'travellerDashboard.demo',
    defaultMessage: 'Demo account view · no live booking, payment or reservation is stored.',
    description: 'Caveat in the traveller dashboard',
  },
  days: {
    id: 'travellerDashboard.days',
    defaultMessage: '{days} days',
    description: 'Trip length in the dashboard',
  },
  forecast: {
    id: 'travellerDashboard.forecast',
    defaultMessage: 'Forecast',
    description: 'Label for the forecast in the dashboard details',
  },
  visa: {
    id: 'travellerDashboard.visa',
    defaultMessage: 'Visa lane',
    description: 'Label for the visa lane in the dashboard details',
  },
  style: {
    id: 'travellerDashboard.style',
    defaultMessage: 'Trip feel',
    description: 'Label for the trip style in the dashboard details',
  },
  food: {
    id: 'travellerDashboard.food',
    defaultMessage: 'Food',
    description: 'Label for the dining rhythm in the dashboard details',
  },
  pace: {
    id: 'travellerDashboard.pace',
    defaultMessage: 'Pace',
    description: 'Label for the daily pace in the dashboard details',
  },
  budgetMix: {
    id: 'travellerDashboard.budgetMix',
    defaultMessage: 'Budget mix',
    description: 'Label for the budget mix in the dashboard details',
  },
  day: {
    id: 'travellerDashboard.day',
    defaultMessage: 'Day {day}',
    description: 'Itinerary day label in the dashboard',
  },
  noSelection: {
    id: 'travellerDashboard.noSelection',
    defaultMessage: 'Waiting for your selection',
    description: 'Placeholder for a stage not selected yet',
  },
});

interface TravellerDashboardDrawerProps {
  onClose: () => void;
}

export function TravellerDashboardDrawer({ onClose }: TravellerDashboardDrawerProps): JSX.Element {
  const intl = useIntl();
  const trip = useBookingDashboard();
  const stageItems = [
    { Icon: Plane, label: intl.formatMessage(messages.flight), value: trip?.flightLabel || '', complete: (trip?.stage ?? 0) >= 1, active: (trip?.stage ?? 0) === 0 },
    { Icon: Hotel, label: intl.formatMessage(messages.hotel), value: trip?.hotelLabel || '', complete: (trip?.stage ?? 0) >= 2, active: (trip?.stage ?? 0) === 1 },
    { Icon: FileText, label: intl.formatMessage(messages.details), value: trip?.days ? intl.formatMessage(messages.days, { days: trip.days }) : '', complete: (trip?.stage ?? 0) >= 3, active: (trip?.stage ?? 0) === 2 },
  ];

  return (
    <>
      <div
        className="fixed inset-0 z-[20] bg-foreground/25 backdrop-blur-[8px] transition-opacity"
        aria-hidden="true"
        onMouseDown={onClose}
      />
      <aside
        className="fixed inset-y-0 right-0 z-[21] flex w-full max-w-md flex-col border-l border-border bg-background text-foreground shadow-2xl tracking-[0.3px]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="traveller-dashboard-heading"
        data-wayfarer-dashboard="true"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border p-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3px] text-primary">Wayfarer</p>
            <h2 id="traveller-dashboard-heading" className="mt-1 text-xl font-bold tracking-tight text-foreground">{intl.formatMessage(messages.title)}</h2>
          </div>
          <button
            type="button"
            className="grid h-9 w-9 place-items-center rounded-full border border-border bg-card text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            aria-label={intl.formatMessage(messages.close)}
            onClick={onClose}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {trip ? (
          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            <section aria-labelledby="traveller-dashboard-trip" className="rounded-[15px] border border-border bg-card p-5">
              <p id="traveller-dashboard-trip" className="text-xs font-bold uppercase tracking-[0.3px] text-primary">{intl.formatMessage(messages.currentTrip)}</p>
              <h3 className="mt-2 break-words text-xl font-bold tracking-tight text-foreground">{[trip.destinationName, trip.destinationCountry].filter(Boolean).join(', ') || trip.planName}</h3>
              {trip.city ? <p className="mt-1 text-sm text-muted-foreground">{trip.city}</p> : null}
              <p className="mt-3 text-sm font-semibold text-foreground">{trip.planName}</p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
                {trip.days ? <span className="rounded-full bg-muted px-2.5 py-1">{intl.formatMessage(messages.days, { days: trip.days })}</span> : null}
                {trip.origin ? <span className="rounded-full bg-muted px-2.5 py-1">{trip.origin}</span> : null}
                {trip.planType ? <span className="rounded-full bg-muted px-2.5 py-1 capitalize">{trip.planType}</span> : null}
              </div>
              <div className="mt-4 rounded-[15px] border border-border bg-background/70 p-5">
                <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2">
                  {[
                    [intl.formatMessage(messages.forecast), trip.season],
                    [intl.formatMessage(messages.visa), trip.visa],
                    [intl.formatMessage(messages.style), trip.style],
                    [intl.formatMessage(messages.food), trip.dining],
                    [intl.formatMessage(messages.pace), trip.pace],
                    [intl.formatMessage(messages.budgetMix), trip.budgetMix],
                  ].filter(([, value]) => Boolean(value)).map(([label, value]) => (
                    <div key={label} className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-[0.3px] text-foreground">{label}</p>
                      <p className="mt-1 break-words text-sm leading-relaxed text-foreground">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="mt-6" aria-labelledby="traveller-dashboard-status">
              <div className="flex items-center justify-between gap-3">
                <h3 id="traveller-dashboard-status" className="text-sm font-bold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.status)}</h3>
                <span className="text-xs font-semibold text-primary">{trip.stage >= 3 ? intl.formatMessage(messages.ready) : `${trip.stage + 1}/3`}</span>
              </div>
              <div className="mt-3 grid gap-2">
                {stageItems.map(({ Icon, label, value, complete, active }) => (
                  <div key={label} className={`rounded-[15px] border p-5 ${complete ? 'border-primary/30 bg-primary/10' : active ? 'border-primary/55 bg-card' : 'border-border bg-card/60'}`}>
                    <div className="flex items-start gap-3">
                      <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${complete ? 'bg-primary text-primary-foreground' : active ? 'bg-primary/12 text-primary' : 'bg-muted text-muted-foreground'}`}>
                        {complete ? <Check className="h-4 w-4" aria-hidden="true" /> : <Icon className="h-4 w-4" aria-hidden="true" />}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-foreground">{label}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{complete ? intl.formatMessage(messages.selected) : active ? intl.formatMessage(messages.inProgress) : intl.formatMessage(messages.next)}</p>
                        <p className="mt-1 break-words text-xs text-muted-foreground">{value || intl.formatMessage(messages.noSelection)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-6" aria-labelledby="traveller-dashboard-itinerary">
              <div className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-primary" aria-hidden="true" />
                <h3 id="traveller-dashboard-itinerary" className="text-sm font-bold uppercase tracking-caps text-muted-foreground">{intl.formatMessage(messages.itinerary)}</h3>
              </div>
              <div className="mt-3 grid gap-2">
                {trip.itinerary.slice(0, 12).map((item) => (
                  <div key={item.day} className="rounded-[15px] border border-border bg-card p-5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-primary">{intl.formatMessage(messages.day, { day: item.day })}</span>
                      {item.summary ? <span className="truncate text-sm font-semibold text-foreground">{item.summary}</span> : null}
                    </div>
                    <div className="mt-2 grid gap-1.5">
                      {item.slots.slice(0, 5).map((slot) => (
                        <div key={`${item.day}-${slot.time}-${slot.detail}`} className="grid grid-cols-[2.75rem_1fr] gap-2 text-sm leading-relaxed">
                          <span className="font-semibold tabular-nums text-primary">{slot.time}</span>
                          <span className="text-muted-foreground">{slot.detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {trip.remainingDays > 0 ? <p className="mt-2 text-sm leading-relaxed text-muted-foreground">+ {trip.remainingDays} more days follow the same itinerary rhythm.</p> : null}
            </section>
          </div>
        ) : (
          <div className="flex flex-1 flex-col justify-center p-5">
            <div className="rounded-[15px] border border-border bg-card p-5">
              <span className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 text-primary"><Clock3 className="h-5 w-5" aria-hidden="true" /></span>
              <h3 className="mt-4 text-xl font-bold tracking-tight text-foreground">{intl.formatMessage(messages.emptyTitle)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{intl.formatMessage(messages.emptyBody)}</p>
            </div>
          </div>
        )}

        <p className="border-t border-border p-5 text-xs leading-relaxed text-muted-foreground">{intl.formatMessage(messages.demo)}</p>
      </aside>
    </>
  );
}
